import React, { ReactNode, useEffect, useState, useCallback } from 'react';
import { usePlaygroundClient, PlaygroundConfig } from './usePlaygroundClient';
import { Plugin } from './types';

export interface PlaygroundProviderProps {
  children: ReactNode;
  config?: PlaygroundConfig;
  onPluginUpdate?: (plugin: Plugin) => void;
  onCodeUpdate?: (pluginName: string, code: string) => void;
  onPluginError?: (pluginName: string, error: string) => void;
  onConnectionChange?: (isConnected: boolean) => void;
  showDebugPanel?: boolean;
  showMinimalIndicator?: boolean;
  debugPanelPosition?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
}

// Safe environment variable access
const getEnvVar = (key: string, fallback: string = ''): string => {
  if (typeof process !== 'undefined' && process.env) {
    return process.env[key] || fallback;
  }
  return fallback;
};

const isDevEnvironment = (): boolean => {
  return getEnvVar('NODE_ENV') === 'development';
};

const defaultConfig: PlaygroundConfig = {
  enabled: isDevEnvironment() || (typeof window !== 'undefined' && window.location.search.includes('playground=true')),
  wsUrl: getEnvVar('REACT_APP_PLAYGROUND_WS_URL', 'ws://localhost:11400'),
  port: parseInt(getEnvVar('REACT_APP_PLAYGROUND_PORT', '11400')),
  reconnectInterval: 3000,
  debug: getEnvVar('REACT_APP_PLAYGROUND_DEBUG') === 'true' || (typeof window !== 'undefined' && window.location.search.includes('playground-debug=true'))
};

export const PlaygroundProvider: React.FC<PlaygroundProviderProps> = ({
  children,
  config = defaultConfig,
  onPluginUpdate,
  onCodeUpdate,
  onPluginError,
  onConnectionChange,
  showDebugPanel = config?.debug || false,
  showMinimalIndicator = true,
  debugPanelPosition = 'top-right'
}) => {
  const [notifications, setNotifications] = useState<Array<{
    id: string;
    type: 'success' | 'error' | 'info';
    message: string;
    timestamp: number;
  }>>([]);
  
  const [showFullPanel, setShowFullPanel] = useState(false);

  const addNotification = useCallback((type: 'success' | 'error' | 'info', message: string) => {
    const notification = {
      id: Date.now().toString(),
      type,
      message,
      timestamp: Date.now()
    };
    
    setNotifications(prev => [...prev, notification]);
    
    // Auto-remove notification after 5 seconds
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== notification.id));
    }, 5000);
  }, []);

  const handlePluginUpdate = useCallback((plugin: Plugin) => {
    addNotification('success', `Plugin "${plugin.meta.name}" updated successfully`);
    onPluginUpdate?.(plugin);
  }, [addNotification, onPluginUpdate]);

  const handlePluginError = useCallback((pluginName: string, error: string) => {
    addNotification('error', `Plugin "${pluginName}" error: ${error}`);
    onPluginError?.(pluginName, error);
  }, [addNotification, onPluginError]);

  const { state, connect, disconnect, sendPing, isEnabled } = usePlaygroundClient(
    config,
    handlePluginUpdate,
    onCodeUpdate,
    handlePluginError
  );

  // Notify parent about connection changes
  useEffect(() => {
    onConnectionChange?.(state.isConnected);
  }, [state.isConnected, onConnectionChange]);

  // Add global styles for playground UI
  useEffect(() => {
    if (!isEnabled) return;

    const style = document.createElement('style');
    style.textContent = `
      .playground-debug-panel {
        position: fixed;
        z-index: 10000;
        background: rgba(0, 0, 0, 0.9);
        color: white;
        padding: 12px;
        border-radius: 8px;
        font-family: monospace;
        font-size: 12px;
        max-width: 300px;
        backdrop-filter: blur(4px);
        border: 1px solid rgba(255, 255, 255, 0.2);
      }
      
      .playground-debug-panel.top-left { top: 10px; left: 10px; }
      .playground-debug-panel.top-right { top: 10px; right: 10px; }
      .playground-debug-panel.bottom-left { bottom: 10px; left: 10px; }
      .playground-debug-panel.bottom-right { bottom: 10px; right: 10px; }
      
      .playground-status {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 8px;
      }
      
      .playground-status-indicator {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        flex-shrink: 0;
      }
      
      .playground-status-indicator.connected { background: #00ff00; }
      .playground-status-indicator.connecting { background: #ffff00; animation: pulse 1s infinite; }
      .playground-status-indicator.disconnected { background: #ff0000; }
      
      @keyframes pulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.5; }
      }
      
      .playground-actions {
        display: flex;
        gap: 8px;
        margin-top: 8px;
      }
      
      .playground-button {
        background: rgba(255, 255, 255, 0.2);
        border: 1px solid rgba(255, 255, 255, 0.3);
        color: white;
        padding: 4px 8px;
        border-radius: 4px;
        cursor: pointer;
        font-size: 10px;
      }
      
      .playground-button:hover {
        background: rgba(255, 255, 255, 0.3);
      }
      
      .playground-button:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
      
      .playground-notifications {
        position: fixed;
        top: 10px;
        right: 10px;
        z-index: 10001;
        display: flex;
        flex-direction: column;
        gap: 8px;
        max-width: 400px;
      }
      
      .playground-notification {
        padding: 12px 16px;
        border-radius: 6px;
        font-size: 14px;
        font-weight: 500;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
        animation: slideIn 0.3s ease-out;
      }
      
      .playground-notification.success {
        background: #10b981;
        color: white;
      }
      
      .playground-notification.error {
        background: #ef4444;
        color: white;
      }
      
      .playground-notification.info {
        background: #3b82f6;
        color: white;
      }
      
      .playground-minimal-indicator {
        position: fixed;
        top: 10px;
        right: 10px;
        z-index: 10000;
        display: flex;
        align-items: center;
        gap: 6px;
        background: rgba(0, 0, 0, 0.7);
        color: white;
        padding: 6px 10px;
        border-radius: 20px;
        font-family: monospace;
        font-size: 11px;
        backdrop-filter: blur(4px);
        border: 1px solid rgba(255, 255, 255, 0.2);
        cursor: pointer;
        transition: all 0.2s ease;
      }
      
      .playground-minimal-indicator:hover {
        background: rgba(0, 0, 0, 0.9);
        transform: scale(1.05);
      }
      
      .playground-minimal-indicator .status-dot {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        flex-shrink: 0;
      }
      
      .playground-minimal-indicator .status-dot.connected { background: #00ff00; }
      .playground-minimal-indicator .status-dot.connecting { background: #ffff00; animation: pulse 1s infinite; }
      .playground-minimal-indicator .status-dot.disconnected { background: #ff0000; }
      
      @keyframes slideIn {
        from {
          transform: translateX(100%);
          opacity: 0;
        }
        to {
          transform: translateX(0);
          opacity: 1;
        }
      }
    `;
    
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, [isEnabled]);

  if (!isEnabled) {
    return <>{children}</>;
  }

  const getStatusText = () => {
    if (state.isConnecting) return 'Connecting...';
    if (state.isConnected) return 'Connected';
    if (state.error) return `Error: ${state.error}`;
    return 'Disconnected';
  };

  const getStatusClass = () => {
    if (state.isConnecting) return 'connecting';
    if (state.isConnected) return 'connected';
    return 'disconnected';
  };

  return (
    <>
      {children}
      
      {/* Minimal Indicator (shown when debug panel is off but minimal indicator is on) */}
      {!showDebugPanel && showMinimalIndicator && !showFullPanel && (
        <div 
          className="playground-minimal-indicator"
          onClick={() => setShowFullPanel(true)}
          title="Click to expand playground panel"
        >
          <div className={`status-dot ${getStatusClass()}`} />
          <span>Playground</span>
        </div>
      )}
      
      {/* Full Debug Panel (shown when debug panel is on OR when minimal indicator is expanded) */}
      {(showDebugPanel || showFullPanel) && (
        <div className={`playground-debug-panel ${debugPanelPosition}`}>
          <div className="playground-status">
            <div className={`playground-status-indicator ${getStatusClass()}`} />
            <span>Playground: {getStatusText()}</span>
            {/* Add close button when showing expanded minimal indicator */}
            {!showDebugPanel && showFullPanel && (
              <button 
                className="playground-button" 
                onClick={() => setShowFullPanel(false)}
                style={{ marginLeft: 'auto', padding: '2px 6px' }}
              >
                ✕
              </button>
            )}
          </div>
          
          {state.isConnected && (
            <div style={{ fontSize: '10px', opacity: 0.8 }}>
              <div>Plugins: {state.receivedPlugins.length}</div>
              {state.lastUpdate && (
                <div>Last update: {new Date(state.lastUpdate).toLocaleTimeString()}</div>
              )}
            </div>
          )}
          
          <div className="playground-actions">
            {state.isConnected ? (
              <>
                <button 
                  className="playground-button" 
                  onClick={disconnect}
                >
                  Disconnect
                </button>
                <button 
                  className="playground-button" 
                  onClick={sendPing}
                >
                  Ping
                </button>
              </>
            ) : (
              <button 
                className="playground-button" 
                onClick={connect}
                disabled={state.isConnecting}
              >
                {state.isConnecting ? 'Connecting...' : 'Connect'}
              </button>
            )}
          </div>
        </div>
      )}
      
      {/* Notifications */}
      {notifications.length > 0 && (
        <div className="playground-notifications">
          {notifications.map(notification => (
            <div
              key={notification.id}
              className={`playground-notification ${notification.type}`}
            >
              {notification.message}
            </div>
          ))}
        </div>
      )}
    </>
  );
};

// Helper function to detect playground mode
export const isPlaygroundMode = (): boolean => {
  return (
    isDevEnvironment() ||
    (typeof window !== 'undefined' && window.location.search.includes('playground=true')) ||
    Boolean(getEnvVar('REACT_APP_PLAYGROUND_ENABLED'))
  );
};

// Helper function to get playground configuration from URL params and environment
export const getPlaygroundConfig = (): PlaygroundConfig => {
  if (typeof window === 'undefined') {
    return { ...defaultConfig, enabled: false };
  }
  
  const urlParams = new URLSearchParams(window.location.search);
  
  return {
    enabled: isPlaygroundMode(),
    wsUrl: urlParams.get('playground-ws') || getEnvVar('REACT_APP_PLAYGROUND_WS_URL', 'ws://localhost:11400'),
    port: parseInt(urlParams.get('playground-port') || getEnvVar('REACT_APP_PLAYGROUND_PORT', '11400')),
    reconnectInterval: parseInt(urlParams.get('playground-reconnect') || '3000'),
    debug: urlParams.has('playground-debug') || getEnvVar('REACT_APP_PLAYGROUND_DEBUG') === 'true',
    showDebugPanel: urlParams.has('playground-show-debug') || getEnvVar('REACT_APP_PLAYGROUND_SHOW_DEBUG') === 'true',
    showMinimalIndicator: !urlParams.has('playground-hide-indicator') && getEnvVar('REACT_APP_PLAYGROUND_HIDE_INDICATOR') !== 'true',
    debugPanelPosition: (urlParams.get('playground-debug-position') as any) || 'bottom-right'
  };
};
